import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconBan,
  IconCash,
  IconCheck,
  IconDownload,
  IconFileCheck,
  IconFileDescription,
  IconPrinter,
  IconRotateClockwise,
  IconSend,
  IconWritingSign,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { checkInn, checkKpp, formatMoney, normalizeRequisite } from '@erp/shared';
import { ApiError } from '../api/client';
import { unitsOfMeasureApi, vatRatesApi } from '../api/catalogs';
import {
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  invoicesApi,
  type InvoiceInput,
} from '../api/invoices';
import { nomenclatureApi } from '../api/nomenclature';
import { organizationsApi, type OrganizationKind } from '../api/organizations';
import { counterpartiesApi } from '../api/counterparties';
import { employeesApi, hasPassportData } from '../api/employees';
import { powersOfAttorneyApi } from '../api/powers-of-attorney';
import { updsApi } from '../api/upd';
import { actsApi } from '../api/acts';
import { InvoiceLinesEditor, emptyLineRow, type InvoiceLineRow } from '../components/InvoiceLinesEditor';
import { useAuth } from '../auth/AuthContext';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Псевдо-значение Select, кодирующее «создать новую запись с таким названием». */
const CREATE_PREFIX = '__create__:';

function withCreateOption<T extends { id: string; shortName: string }>(
  items: T[],
  search: string,
): Array<{ value: string; label: string }> {
  const options = items.map((item) => ({ value: item.id, label: item.shortName }));
  const trimmed = search.trim();
  if (!trimmed) return options;
  const exists = items.some((item) => item.shortName.toLowerCase() === trimmed.toLowerCase());
  if (exists) return options;
  return [{ value: `${CREATE_PREFIX}${trimmed}`, label: `+ Создать «${trimmed}»` }, ...options];
}

export function InvoiceFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new' || !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const canEditGlobally = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');

  const [organizationId, setOrganizationId] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [date, setDate] = useState(today());
  const [dueDate, setDueDate] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [comment, setComment] = useState('');
  const [footerText, setFooterText] = useState('');
  const [lines, setLines] = useState<InvoiceLineRow[]>([emptyLineRow()]);
  const [formError, setFormError] = useState<string[] | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [poaModalOpen, setPoaModalOpen] = useState(false);
  const [poaEmployeeId, setPoaEmployeeId] = useState('');

  const [organizationSearch, setOrganizationSearch] = useState('');
  const [counterpartySearch, setCounterpartySearch] = useState('');
  const [quickOrgModalOpen, setQuickOrgModalOpen] = useState(false);
  const [quickOrgName, setQuickOrgName] = useState('');
  const [quickOrgKind, setQuickOrgKind] = useState<OrganizationKind>('LEGAL_ENTITY');
  const [quickOrgInn, setQuickOrgInn] = useState('');
  const [quickOrgKpp, setQuickOrgKpp] = useState('');
  const [quickOrgError, setQuickOrgError] = useState<string | null>(null);

  const loadedFor = useRef<string | null>(null);

  const invoiceQuery = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.get(id!),
    enabled: !isNew,
  });

  const organizations = useQuery({
    queryKey: ['organizations', 'all'],
    queryFn: () => organizationsApi.list({ pageSize: 200 }),
  });
  const counterparties = useQuery({
    queryKey: ['counterparties', 'all'],
    queryFn: () => counterpartiesApi.list({ pageSize: 200 }),
  });
  const units = useQuery({ queryKey: ['units-of-measure'], queryFn: () => unitsOfMeasureApi.list() });
  const vatRates = useQuery({ queryKey: ['vat-rates'], queryFn: () => vatRatesApi.list() });
  const nomenclature = useQuery({
    queryKey: ['nomenclature', 'all'],
    queryFn: () => nomenclatureApi.list({ pageSize: 500 }),
  });
  // Сотрудники нужны только для выписки доверенности — грузим по открытию модалки
  const employees = useQuery({
    queryKey: ['employees', 'all'],
    queryFn: () => employeesApi.list({ pageSize: 500 }),
    enabled: poaModalOpen,
  });

  const organizationDetail = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => organizationsApi.get(organizationId),
    enabled: !!organizationId,
  });

  /**
   * Быстрое создание контрагента прямо из счёта: розничный покупатель — почти
   * всегда физлицо, которому для документа хватает ФИО (ИНН не требуется —
   * см. правку validateRequisites). Заводить его через отдельный справочник
   * ради одной строки в счёте было бы лишним кликом на каждую продажу.
   */
  const createCounterparty = useMutation({
    mutationFn: (name: string) => counterpartiesApi.create({ shortName: name, fullName: name, kind: 'INDIVIDUAL' }),
    onSuccess: (data) => {
      queryClient.setQueryData(['counterparties', 'all'], (old: typeof counterparties.data) =>
        old ? { ...old, items: [data.counterparty, ...old.items] } : old,
      );
      setCounterpartyId(data.counterparty.id);
      setCounterpartySearch('');
      notifications.show({ color: 'green', message: `Контрагент «${data.counterparty.shortName}» создан` });
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? (err.details?.join(', ') ?? err.message) : 'Не удалось создать контрагента',
      });
    },
  });

  // У организации (своего юрлица/ИП) ИНН обязателен и проверяется по контрольной
  // сумме — в одно поле такое не завести, поэтому здесь короткая форма, а не
  // мгновенное создание по одному имени, как для контрагента.
  const createOrganization = useMutation({
    mutationFn: () =>
      organizationsApi.create({
        shortName: quickOrgName,
        fullName: quickOrgName,
        kind: quickOrgKind,
        taxationSystem: 'OSNO',
        inn: normalizeRequisite(quickOrgInn),
        kpp: quickOrgKind === 'LEGAL_ENTITY' ? normalizeRequisite(quickOrgKpp) : null,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['organizations', 'all'], (old: typeof organizations.data) =>
        old ? { ...old, items: [data.organization, ...old.items] } : old,
      );
      setOrganizationId(data.organization.id);
      setOrganizationSearch('');
      setQuickOrgModalOpen(false);
      notifications.show({ color: 'green', message: `Организация «${data.organization.shortName}» создана` });
    },
    onError: (err) => {
      setQuickOrgError(err instanceof ApiError ? (err.details?.join(', ') ?? err.message) : 'Не удалось создать организацию');
    },
  });

  function submitQuickOrg() {
    setQuickOrgError(null);
    if (!quickOrgName.trim()) return setQuickOrgError('Укажите наименование');
    const innCheck = checkInn(normalizeRequisite(quickOrgInn));
    if (!innCheck.valid) return setQuickOrgError(`ИНН: ${innCheck.error}`);
    if (quickOrgKind === 'LEGAL_ENTITY') {
      const kppCheck = checkKpp(normalizeRequisite(quickOrgKpp));
      if (!kppCheck.valid) return setQuickOrgError(`КПП: ${kppCheck.error}`);
    }
    createOrganization.mutate();
  }

  // Заполняем форму данными существующего счёта один раз при загрузке —
  // иначе фоновый рефетч перезаписывал бы то, что пользователь уже успел набрать.
  useEffect(() => {
    const invoice = invoiceQuery.data?.invoice;
    if (!invoice || loadedFor.current === invoice.id) return;
    loadedFor.current = invoice.id;

    setOrganizationId(invoice.organizationId);
    setCounterpartyId(invoice.counterpartyId);
    setDate(invoice.date.slice(0, 10));
    setDueDate(invoice.dueDate?.slice(0, 10) ?? '');
    setBankAccountId(invoice.bankAccountId ?? '');
    setComment(invoice.comment ?? '');
    setFooterText(invoice.footerText ?? '');
    setLines(
      invoice.lines.map((l) => ({
        key: l.id,
        nomenclatureId: l.nomenclatureId,
        name: l.name,
        article: l.article,
        unitId: l.unitId ?? '',
        quantity: l.quantity,
        price: l.price,
        vatRateId: vatRates.data?.items.find((r) => r.code === l.vatRateCode)?.id ?? '',
      })),
    );
  }, [invoiceQuery.data, vatRates.data]);

  // Для нового счёта подставляем счёт организации по умолчанию — иначе после
  // выбора организации QR-код на печатной форме пропадает, пока бухгалтер
  // не выберет расчётный счёт вручную (для существующего счёта bankAccountId
  // уже задан документом, поэтому здесь трогаем только новый).
  useEffect(() => {
    if (!isNew || bankAccountId) return;
    const defaultAccount = organizationDetail.data?.organization.bankAccounts.find((a) => a.isDefault);
    if (defaultAccount) setBankAccountId(defaultAccount.id);
  }, [isNew, bankAccountId, organizationDetail.data]);

  const invoice = invoiceQuery.data?.invoice ?? null;
  const canEditDocument = canEditGlobally && (isNew || invoice?.status === 'DRAFT');

  const organizationOptions = withCreateOption(organizations.data?.items ?? [], organizationSearch);
  const counterpartyOptions = withCreateOption(counterparties.data?.items ?? [], counterpartySearch);
  const bankAccountOptions = (organizationDetail.data?.organization.bankAccounts ?? []).map((a) => ({
    value: a.id,
    label: `${a.bankName} — ${a.accountNumber}`,
  }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: InvoiceInput = {
        organizationId,
        counterpartyId,
        date,
        dueDate: dueDate || null,
        bankAccountId: bankAccountId || null,
        comment: comment || null,
        footerText: footerText || null,
        lines: lines.map((l) => ({
          nomenclatureId: l.nomenclatureId,
          name: l.name.trim(),
          article: l.article || null,
          unitId: l.unitId,
          quantity: l.quantity,
          price: l.price,
          vatRateId: l.vatRateId,
        })),
      };
      return isNew ? invoicesApi.create(payload) : invoicesApi.update(id!, payload);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['invoice', data.invoice.id] });
      notifications.show({ color: 'green', message: 'Счёт сохранён' });
      if (isNew) navigate(`/invoices/${data.invoice.id}`, { replace: true });
    },
  });

  function handleSubmit() {
    setFormError(null);
    const errors: string[] = [];
    if (!organizationId) errors.push('Укажите организацию');
    if (!counterpartyId) errors.push('Укажите контрагента');
    if (!date) errors.push('Укажите дату');
    if (lines.length === 0) errors.push('Добавьте хотя бы одну строку');
    lines.forEach((l, i) => {
      if (!l.name.trim()) errors.push(`Строка ${i + 1}: укажите наименование`);
      if (!l.unitId) errors.push(`Строка ${i + 1}: укажите единицу измерения`);
      if (!l.vatRateId) errors.push(`Строка ${i + 1}: укажите ставку НДС`);
      if (!l.quantity.trim()) errors.push(`Строка ${i + 1}: укажите количество`);
      if (!l.price.trim()) errors.push(`Строка ${i + 1}: укажите цену`);
    });
    if (errors.length > 0) {
      setFormError(errors);
      return;
    }
    saveMutation.mutate(undefined, {
      onError: (err) => {
        setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
      },
    });
  }

  const issueMutation = useMutation({
    mutationFn: () => invoicesApi.issue(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      notifications.show({ color: 'green', message: 'Счёт выставлен' });
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось выставить счёт' });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => invoicesApi.cancel(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      notifications.show({ color: 'green', message: 'Счёт отменён' });
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось отменить счёт' });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => invoicesApi.restore(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      notifications.show({ color: 'green', message: 'Счёт восстановлен' });
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось восстановить счёт' });
    },
  });

  const createUpdMutation = useMutation({
    mutationFn: () => updsApi.createFromInvoice(id!),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['upds'] });
      notifications.show({ color: 'green', message: `УПД № ${data.upd.number} создан` });
      navigate(`/upd/${data.upd.id}`);
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? (err.details?.join(', ') ?? err.message) : 'Не удалось создать УПД',
      });
    },
  });

  const createActMutation = useMutation({
    mutationFn: () => actsApi.createFromInvoice(id!),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['acts'] });
      notifications.show({ color: 'green', message: `Акт № ${data.act.number} создан` });
      navigate(`/acts/${data.act.id}`);
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? (err.details?.join(', ') ?? err.message) : 'Не удалось создать акт',
      });
    },
  });

  /**
   * Доверенность по счёту выписывается на конкретного водителя — его и
   * спрашиваем, остальное (поставщик, перечень ТМЦ) берётся из счёта.
   */
  const createPoaMutation = useMutation({
    mutationFn: () => powersOfAttorneyApi.createFromInvoice(id!, { employeeId: poaEmployeeId }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['powers-of-attorney'] });
      notifications.show({ color: 'green', message: `Доверенность № ${data.powerOfAttorney.number} выписана` });
      setPoaModalOpen(false);
      navigate(`/powers-of-attorney/${data.powerOfAttorney.id}`);
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? (err.details?.join(', ') ?? err.message) : 'Не удалось выписать доверенность',
      });
    },
  });

  const paymentMutation = useMutation({
    mutationFn: () => invoicesApi.registerPayment(id!, { paidAmount: paymentAmount }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      notifications.show({ color: 'green', message: 'Оплата зарегистрирована' });
      setPaymentModalOpen(false);
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось зарегистрировать оплату' });
    },
  });

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/invoices')}>
            К списку
          </Button>
          <Title order={2}>{isNew ? 'Новый счёт' : `Счёт № ${invoice?.number ?? ''}`}</Title>
          {invoice && (
            <Badge color={INVOICE_STATUS_COLORS[invoice.status]} variant="light">
              {INVOICE_STATUS_LABELS[invoice.status]}
            </Badge>
          )}
        </Group>

        {!isNew && invoice && (
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              component="a"
              href={`/invoices/${invoice.id}/print`}
              target="_blank"
            >
              Печать
            </Button>
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              onClick={() => window.open(`/invoices/${invoice.id}/print?download=1`, '_blank')}
            >
              Скачать PDF
            </Button>
            {canEditGlobally && invoice.status === 'DRAFT' && (
              <Button
                leftSection={<IconSend size={16} />}
                onClick={() => issueMutation.mutate()}
                loading={issueMutation.isPending}
              >
                Выставить
              </Button>
            )}
            {canEditGlobally && invoice.status !== 'DRAFT' && invoice.status !== 'CANCELLED' && (
              <Button
                leftSection={<IconFileDescription size={16} />}
                variant="light"
                onClick={() => createUpdMutation.mutate()}
                loading={createUpdMutation.isPending}
              >
                Создать УПД
              </Button>
            )}
            {canEditGlobally && invoice.status !== 'DRAFT' && invoice.status !== 'CANCELLED' && (
              <Button
                leftSection={<IconFileCheck size={16} />}
                variant="light"
                onClick={() => createActMutation.mutate()}
                loading={createActMutation.isPending}
              >
                Создать акт
              </Button>
            )}
            {canEditGlobally && invoice.status !== 'DRAFT' && invoice.status !== 'CANCELLED' && (
              <Button
                leftSection={<IconWritingSign size={16} />}
                variant="light"
                onClick={() => {
                  setPoaEmployeeId('');
                  setPoaModalOpen(true);
                }}
              >
                Доверенность
              </Button>
            )}
            {canEditGlobally && (invoice.status === 'ISSUED' || invoice.status === 'PARTIALLY_PAID') && (
              <Button
                leftSection={<IconCash size={16} />}
                variant="light"
                onClick={() => {
                  setPaymentAmount(invoice.paidAmount);
                  setPaymentModalOpen(true);
                }}
              >
                Оплата
              </Button>
            )}
            {canEditGlobally && (invoice.status === 'ISSUED' || invoice.status === 'PARTIALLY_PAID') && (
              <Button
                leftSection={<IconBan size={16} />}
                color="red"
                variant="light"
                onClick={() => cancelMutation.mutate()}
                loading={cancelMutation.isPending}
              >
                Отменить
              </Button>
            )}
            {canEditGlobally && invoice.status === 'CANCELLED' && (
              <Button
                leftSection={<IconRotateClockwise size={16} />}
                variant="light"
                onClick={() => restoreMutation.mutate()}
                loading={restoreMutation.isPending}
              >
                Восстановить
              </Button>
            )}
          </Group>
        )}
      </Group>

      {formError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          <Stack gap={2}>
            {formError.map((e, i) => (
              <Text key={i} size="sm">
                {e}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      <Group grow align="flex-start">
        <Select
          label="Организация"
          placeholder="Выберите или введите новое название"
          data={organizationOptions}
          value={organizationId || null}
          onSearchChange={setOrganizationSearch}
          onChange={(v) => {
            if (v?.startsWith(CREATE_PREFIX)) {
              setQuickOrgName(v.slice(CREATE_PREFIX.length));
              setQuickOrgKind('LEGAL_ENTITY');
              setQuickOrgInn('');
              setQuickOrgKpp('');
              setQuickOrgError(null);
              setQuickOrgModalOpen(true);
              return;
            }
            setOrganizationId(v ?? '');
            setBankAccountId('');
          }}
          disabled={!canEditDocument}
          searchable
          required
        />
        <Select
          label="Контрагент"
          placeholder="Выберите или введите ФИО / название"
          data={counterpartyOptions}
          value={counterpartyId || null}
          onSearchChange={setCounterpartySearch}
          onChange={(v) => {
            if (v?.startsWith(CREATE_PREFIX)) {
              createCounterparty.mutate(v.slice(CREATE_PREFIX.length));
              return;
            }
            setCounterpartyId(v ?? '');
          }}
          disabled={!canEditDocument}
          searchable
          required
        />
      </Group>

      <Group grow align="flex-start">
        <TextInput
          label="Дата"
          type="date"
          value={date}
          onChange={(e) => setDate(e.currentTarget.value)}
          disabled={!canEditDocument}
          required
        />
        <TextInput
          label="Счёт действителен до"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
        <Select
          label="Расчётный счёт"
          placeholder={organizationId ? 'Выберите счёт' : 'Сначала выберите организацию'}
          data={bankAccountOptions}
          value={bankAccountId || null}
          onChange={(v) => setBankAccountId(v ?? '')}
          disabled={!canEditDocument || !organizationId}
          clearable
        />
      </Group>

      <InvoiceLinesEditor
        lines={lines}
        onChange={setLines}
        canEdit={canEditDocument}
        nomenclatureOptions={nomenclature.data?.items ?? []}
        units={units.data?.items ?? []}
        vatRates={vatRates.data?.items ?? []}
      />

      {invoice && (
        <Text size="sm" c="dimmed">
          Оплачено: {formatMoney(invoice.paidAmount)} из {formatMoney(invoice.total)}
        </Text>
      )}

      {invoice && invoice.upds.length > 0 && (
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            УПД по счёту:
          </Text>
          {invoice.upds.map((u) => (
            <Button key={u.id} size="xs" variant="light" onClick={() => navigate(`/upd/${u.id}`)}>
              № {u.number}
            </Button>
          ))}
        </Group>
      )}

      {invoice && invoice.acts.length > 0 && (
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            Акты по счёту:
          </Text>
          {invoice.acts.map((a) => (
            <Button key={a.id} size="xs" variant="light" onClick={() => navigate(`/acts/${a.id}`)}>
              № {a.number}
            </Button>
          ))}
        </Group>
      )}

      <Textarea
        label="Комментарий"
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
        disabled={!canEditDocument}
        autosize
        minRows={1}
      />
      <Textarea
        label="Текст под таблицей (условия поставки и т.п.)"
        value={footerText}
        onChange={(e) => setFooterText(e.currentTarget.value)}
        disabled={!canEditDocument}
        autosize
        minRows={1}
      />

      {canEditDocument && (
        <Group>
          <Button leftSection={<IconCheck size={16} />} onClick={handleSubmit} loading={saveMutation.isPending}>
            Сохранить
          </Button>
        </Group>
      )}

      <Modal opened={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} title="Регистрация оплаты">
        <Stack gap="sm">
          <TextInput
            label="Оплачено всего"
            description="Итоговая сумма поступлений по счёту, а не приращение"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.currentTarget.value)}
          />
          <Button onClick={() => paymentMutation.mutate()} loading={paymentMutation.isPending}>
            Сохранить
          </Button>
        </Stack>
      </Modal>

      <Modal opened={poaModalOpen} onClose={() => setPoaModalOpen(false)} title="Доверенность на получение ТМЦ">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Поставщик и перечень ценностей будут перенесены из счёта. Срок действия — 15 дней с
            сегодняшнего дня, изменить его можно в карточке доверенности.
          </Text>
          <Select
            label="Кому выдать"
            placeholder="Выберите сотрудника"
            data={(employees.data?.items ?? []).map((e) => ({
              value: e.id,
              label: e.position ? `${e.fullName} — ${e.position}` : e.fullName,
            }))}
            value={poaEmployeeId || null}
            onChange={(v) => setPoaEmployeeId(v ?? '')}
            searchable
            required
          />
          {(() => {
            const selected = (employees.data?.items ?? []).find((e) => e.id === poaEmployeeId);
            return selected && !hasPassportData(selected) ? (
              <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
                У сотрудника не заполнены паспортные данные — заполните карточку в справочнике
                «Сотрудники», иначе доверенность не выписать.
              </Alert>
            ) : null;
          })()}
          <Button
            leftSection={<IconWritingSign size={16} />}
            onClick={() => createPoaMutation.mutate()}
            loading={createPoaMutation.isPending}
            disabled={!poaEmployeeId}
          >
            Выписать доверенность
          </Button>
        </Stack>
      </Modal>

      <Modal opened={quickOrgModalOpen} onClose={() => setQuickOrgModalOpen(false)} title="Новая организация">
        <Stack gap="sm">
          {quickOrgError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {quickOrgError}
            </Alert>
          )}
          <TextInput
            label="Наименование"
            value={quickOrgName}
            onChange={(e) => setQuickOrgName(e.currentTarget.value)}
            required
          />
          <Select
            label="Вид"
            data={[
              { value: 'LEGAL_ENTITY', label: 'Юридическое лицо' },
              { value: 'ENTREPRENEUR', label: 'Индивидуальный предприниматель' },
            ]}
            value={quickOrgKind}
            onChange={(v) => setQuickOrgKind((v as OrganizationKind) ?? quickOrgKind)}
            allowDeselect={false}
          />
          <Group grow>
            <TextInput label="ИНН" value={quickOrgInn} onChange={(e) => setQuickOrgInn(e.currentTarget.value)} required />
            {quickOrgKind === 'LEGAL_ENTITY' && (
              <TextInput
                label="КПП"
                value={quickOrgKpp}
                onChange={(e) => setQuickOrgKpp(e.currentTarget.value)}
                required
              />
            )}
          </Group>
          <Text size="xs" c="dimmed">
            Остальные реквизиты (адрес, банк, подписанты) можно дозаполнить позже в разделе «Организации».
          </Text>
          <Button onClick={submitQuickOrg} loading={createOrganization.isPending}>
            Создать
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
